// Supabase Auth configuration
// Replaces Clerk authentication with native Supabase Auth

import { createClient, SupabaseClient, Session, User } from '@supabase/supabase-js'
import type { Database } from '../../database.types'

// Environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required')
}

// Create the main Supabase client with auth persistence
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
})

// Auth helper types that match our existing interface
export interface AuthUser {
  id: string
  email?: string | null
  user_metadata?: {
    full_name?: string
    avatar_url?: string
    [key: string]: unknown
  }
}

export interface AuthSession {
  user: AuthUser | null
  access_token: string | null
  refresh_token?: string | null
}

// Convert Supabase User to our AuthUser interface
export function toAuthUser(user: User | null): AuthUser | null {
  if (!user) return null

  return {
    id: user.id,
    email: user.email,
    user_metadata: {
      full_name: user.user_metadata?.full_name as string | undefined,
      avatar_url: user.user_metadata?.avatar_url as string | undefined,
      ...user.user_metadata,
    },
  }
}

// Convert Supabase Session to our AuthSession interface
export function toAuthSession(session: Session | null): AuthSession | null {
  if (!session) return null

  return {
    user: toAuthUser(session.user),
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  }
}

// Auth functions
export async function signInWithEmail(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  if (error) throw error
  return data
}

export async function signUpWithEmail(email: string, password: string, metadata?: { full_name?: string }) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
    },
  })

  if (error) throw error
  return data
}

export async function signInWithOAuth(provider: 'google' | 'github') {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/dashboard`,
    },
  })

  if (error) throw error
  return data
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function resetPassword(email: string) {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })

  if (error) throw error
  return data
}

export async function updatePassword(newPassword: string) {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  })

  if (error) throw error
  return data
}

export async function getSession() {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session
}

export async function getUser() {
  const { data, error } = await supabase.auth.getUser()
  if (error) throw error
  return data.user
}

// Export the typed Supabase client
export type TypedSupabaseClient = SupabaseClient<Database>
export { supabaseUrl, supabaseAnonKey }
